/**
 * React Flow 自定义节点：PromptCard
 * 8 字段表单 + 拼装预览 + 生成按钮
 * 生成成功后自动创建 ImageNode + 连接 Edge
 */

import { Handle, Position, useReactFlow, type NodeProps, type Node } from '@xyflow/react'
import { useCallback, useMemo, useState } from 'react'
import { canvasApi, type PromptFields } from '../../utils/canvas-api'
import { useCanvasStore } from '../../stores/canvas'
import { useConfirmStore } from './ConfirmDialog'
import { reflowChildren, CHILD_X } from './canvas-layout'
import { AutoGrowTextarea } from './AutoGrowTextarea'
import { PresetDropdown } from './PresetDropdown'
import { PROMPT_PRESETS, type PresetKey } from './prompt-presets'
import { assemblePreview, estimateTokens, TOKEN_RECOMMEND_MAX } from './prompt-utils'

export interface PromptCardData extends Record<string, unknown> {
  fields: Required<PromptFields>
  promptMode?: 'simple' | 'pro'
  simplePrompt?: string
  isGenerating: boolean
  lastError: string
  collapsed?: boolean
  aiPrompt?: string
  isOptimizing?: boolean
  optimizeError?: string
  useAiPrompt?: boolean
  model?: string
  seed?: string
  batchCount?: 1 | 2 | 4
  generationProgress?: string
  fieldGroupsOpen?: Record<string, boolean>
}

export type PromptCardNodeType = Node<PromptCardData, 'prompt-card'>

const FIELD_DEFS: Array<{ key: keyof Required<PromptFields>; label: string; placeholder: string; icon: string }> = [
  { key: 'subject', label: '主体', placeholder: '一位卷发黑发的 30 岁摩洛哥女性', icon: '🧑' },
  { key: 'action', label: '动作 / 姿态', placeholder: '在天台上啜饮薄荷茶', icon: '🎬' },
  { key: 'environment', label: '环境', placeholder: '黄昏时分俯瞰非斯老城', icon: '🌍' },
  { key: 'composition', label: '构图', placeholder: '中景对称构图，主体居中，留白克制', icon: '📐' },
  { key: 'lighting', label: '光照', placeholder: '暖色定向阳光，长长的阴影', icon: '💡' },
  { key: 'camera', label: '镜头与焦段', placeholder: '富士 X-T5、35mm、浅景深', icon: '📷' },
  { key: 'color', label: '色彩', placeholder: '暖橘主调 + 靛蓝阴影，电影感对比', icon: '🌈' },
  { key: 'medium', label: '媒介', placeholder: '胶片摄影', icon: '🖼️' },
  { key: 'style', label: '风格参考', placeholder: '杂志摄影、国家地理风', icon: '🎨' },
  { key: 'mood', label: '情绪', placeholder: '安静、沉思、怀旧', icon: '🎭' },
  { key: 'quality', label: '画质标签', placeholder: '超清细节、4K、写实', icon: '⭐' },
]

const FIELD_DEFS_BY_KEY = new Map(FIELD_DEFS.map((field) => [field.key, field]))

const FIELD_GROUPS: Array<{
  key: string
  title: string
  description: string
  fields: Array<keyof Required<PromptFields>>
  defaultOpen: boolean
}> = [
  {
    key: 'scene',
    title: '主体与场景',
    description: '决定画面里有什么、正在发生什么',
    fields: ['subject', 'action', 'environment'],
    defaultOpen: true,
  },
  {
    key: 'camera',
    title: '镜头与光影',
    description: '控制构图、光照和镜头语言',
    fields: ['composition', 'lighting', 'camera'],
    defaultOpen: true,
  },
  {
    key: 'style',
    title: '风格与质量',
    description: '控制媒介、色彩、情绪和成片质感',
    fields: ['color', 'medium', 'style', 'mood', 'quality'],
    defaultOpen: false,
  },
]

const AVAILABLE_IMAGE_MODELS = [
  { value: 'black-forest-labs/flux-schnell', label: 'Flux Schnell' },
  { value: 'black-forest-labs/flux-2-dev', label: 'Flux 2 Dev' },
  { value: 'prunaai/p-image', label: 'P-Image' },
  { value: 'prunaai/z-image-turbo', label: 'Z-Image Turbo' },
]

const BATCH_OPTIONS: Array<1 | 2 | 4> = [1, 2, 4]

export const PROMPT_CARD_DEFAULT_DATA: PromptCardData = {
  promptMode: 'simple',
  simplePrompt: '',
  fields: {
    subject: '',
    action: '',
    environment: '',
    composition: '',
    lighting: '',
    camera: '',
    color: '',
    medium: '',
    style: '',
    mood: '',
    quality: '',
  },
  isGenerating: false,
  lastError: '',
  collapsed: false,
  aiPrompt: '',
  isOptimizing: false,
  optimizeError: '',
  useAiPrompt: false,
  model: 'black-forest-labs/flux-schnell',
  seed: '',
  batchCount: 1,
  generationProgress: '',
  fieldGroupsOpen: {
    scene: true,
    camera: true,
    style: false,
  },
}

export function PromptCardNode({ id, data, selected }: NodeProps<PromptCardNodeType>) {
  const rf = useReactFlow()
  const {
    fields,
    isGenerating,
    lastError,
    collapsed = false,
    aiPrompt = '',
    isOptimizing = false,
    optimizeError = '',
    useAiPrompt = false,
    model = 'black-forest-labs/flux-schnell',
    seed = '',
    batchCount = 1,
    generationProgress = '',
    fieldGroupsOpen = PROMPT_CARD_DEFAULT_DATA.fieldGroupsOpen || {},
  } = data
  const promptMode: 'simple' | 'pro' = data.promptMode === 'simple' || data.promptMode === 'pro' ? data.promptMode : 'pro'
  const simplePrompt = typeof data.simplePrompt === 'string' ? data.simplePrompt : ''
  const [openPresetKey, setOpenPresetKey] = useState<PresetKey | null>(null)

  const preview = useMemo(() => assemblePreview(fields), [fields])
  const previewTokens = useMemo(() => estimateTokens(preview), [preview])
  const aiTokens = useMemo(() => estimateTokens(aiPrompt), [aiPrompt])
  const simpleTokens = useMemo(() => estimateTokens(simplePrompt), [simplePrompt])
  const professionalPrompt = useAiPrompt && aiPrompt ? aiPrompt : preview
  const effectivePrompt = promptMode === 'simple' ? simplePrompt : professionalPrompt

  const updateData = useCallback(
    (patch: Partial<PromptCardData>) => {
      rf.setNodes((nodes) =>
        nodes.map((n) => (n.id === id ? { ...n, data: { ...(n.data as PromptCardData), ...patch } } : n)),
      )
    },
    [rf, id],
  )

  const updateField = useCallback(
    (key: keyof Required<PromptFields>, value: string) => {
      updateData({ fields: { ...fields, [key]: value } })
    },
    [updateData, fields],
  )

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return
    if (!effectivePrompt.trim()) {
      updateData({ lastError: promptMode === 'simple' ? '请先输入提示词' : '请先填写至少一个字段' })
      return
    }

    const normalizedSeed = seed.trim()
    const baseSeed: number | null = normalizedSeed ? Number(normalizedSeed) : null
    if (baseSeed !== null && (!Number.isInteger(baseSeed) || baseSeed < 0)) {
      updateData({ lastError: 'Seed 必须是非负整数' })
      return
    }

    const count = BATCH_OPTIONS.includes(batchCount) ? batchCount : 1
    updateData({ isGenerating: true, lastError: '', generationProgress: count > 1 ? `准备生成 ${count} 张图片…` : '' })

    try {
      const canvasId = useCanvasStore.getState().canvasId
      const existingChildCount = rf.getNodes().filter((n) => n.parentId === id && n.type === 'image-node').length

      for (let index = 0; index < count; index++) {
        updateData({
          generationProgress: count > 1 ? `正在生成第 ${index + 1}/${count} 张图片…` : '',
        })
        const requestSeed = baseSeed !== null ? baseSeed + index : count > 1 ? Math.floor(Math.random() * 1000000) : undefined
        const requestFields =
          promptMode === 'simple'
            ? { ...PROMPT_CARD_DEFAULT_DATA.fields, subject: effectivePrompt.trim() }
            : fields
        const result = await canvasApi.generate({
          prompt: effectivePrompt,
          fields: requestFields,
          canvas_id: canvasId || undefined,
          model,
          seed: requestSeed,
        })

        const offsetY = (existingChildCount + index) * 392 // 临时位置，下一帧由 reflow 校正
        const newId = `image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

        rf.setNodes((nodes) => [
          ...nodes,
          {
            id: newId,
            type: 'image-node',
            parentId: id,
            position: { x: CHILD_X, y: offsetY },
            dragHandle: '.drag-handle',
            data: {
              imageUrl: result.url,
              prompt: result.prompt,
              generationId: result.id,
              model: result.model,
              seed: requestSeed,
              collapsed: false,
            },
          },
        ])

        rf.setEdges((edges) => [
          ...edges,
          {
            id: `${id}->${newId}`,
            source: id,
            target: newId,
            animated: true,
            style: { stroke: '#8b5cf6', strokeWidth: 2 },
          },
        ])
      }

      // 按实际折叠态重排所有兄弟节点（覆盖临时硬编码 offsetY）
      reflowChildren(rf, id)

      updateData({ isGenerating: false, lastError: '', generationProgress: '' })
    } catch (err) {
      console.error('Generate failed:', err)
      updateData({
        isGenerating: false,
        generationProgress: '',
        lastError: err instanceof Error ? err.message : '生成失败',
      })
    }
  }, [batchCount, effectivePrompt, fields, id, isGenerating, model, promptMode, rf, seed, updateData])

  const handleOptimize = useCallback(async () => {
    if (isOptimizing) return
    if (!preview.trim()) {
      updateData({ optimizeError: '请先填写至少一个字段' })
      return
    }
    updateData({ isOptimizing: true, optimizeError: '' })
    try {
      const result = await canvasApi.optimize(fields)
      updateData({
        aiPrompt: result.optimized,
        useAiPrompt: true,
        isOptimizing: false,
        optimizeError: '',
      })
    } catch (err) {
      console.error('Optimize failed:', err)
      updateData({
        isOptimizing: false,
        optimizeError: err instanceof Error ? err.message : 'AI 优化失败',
      })
    }
  }, [fields, isOptimizing, preview, updateData])

  const handleDelete = useCallback(() => {
    const childCount = rf.getEdges().filter((e) => e.source === id).length
    useConfirmStore.getState().show({
      title: '删除 Prompt Card',
      message: childCount > 0
        ? `这张卡片下有 ${childCount} 张图片，删除后图片将一并被移除，确认？`
        : '确认删除此 Prompt Card？',
      confirmText: '删除',
      danger: true,
      onConfirm: () => {
        // 同时删除子 ImageNode（parentId 关联）
        const childIds = rf.getNodes().filter((n) => n.parentId === id).map((n) => ({ id: n.id }))
        rf.deleteElements({ nodes: [{ id }, ...childIds] })
      },
    })
  }, [rf, id])

  const handleDuplicate = useCallback(() => {
    const self = rf.getNode(id)
    if (!self) return
    const newId = `card_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const nextData: PromptCardData = {
      ...(self.data as PromptCardData),
      fields: { ...fields },
      isGenerating: false,
      lastError: '',
      isOptimizing: false,
      optimizeError: '',
      generationProgress: '',
    }
    rf.setNodes((nodes) => [
      ...nodes,
      {
        id: newId,
        type: 'prompt-card',
        position: { x: self.position.x + 430, y: self.position.y + 24 },
        dragHandle: '.drag-handle',
        data: nextData,
      },
    ])
  }, [fields, id, rf])

  const handleToggle = useCallback(() => {
    updateData({ collapsed: !collapsed })
  }, [updateData, collapsed])

  const toggleFieldGroup = useCallback(
    (groupKey: string, nextOpen: boolean) => {
      updateData({ fieldGroupsOpen: { ...fieldGroupsOpen, [groupKey]: nextOpen } })
    },
    [fieldGroupsOpen, updateData],
  )

  const switchPromptMode = useCallback(
    (nextMode: 'simple' | 'pro') => {
      if (nextMode === promptMode) return
      if (nextMode === 'simple') {
        updateData({
          promptMode: 'simple',
          simplePrompt: simplePrompt.trim() ? simplePrompt : professionalPrompt,
          lastError: '',
          optimizeError: '',
        })
        return
      }
      updateData({ promptMode: 'pro', lastError: '' })
    },
    [professionalPrompt, promptMode, simplePrompt, updateData],
  )

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ background: '#6366f1', width: 10, height: 10 }} />
      <div
        style={{
          width: 380,
          background: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 12,
          boxShadow: selected
            ? '0 0 0 2px #8b5cf6, 0 4px 24px rgba(0,0,0,0.45)'
            : '0 4px 24px rgba(0,0,0,0.35)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 12,
          overflow: 'hidden',
          border: '1px solid #1e293b',
        }}
      >
        <div
          className="drag-handle"
          style={{
            padding: '10px 14px',
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)',
            color: '#fff',
            fontWeight: 600,
            letterSpacing: 0.3,
            cursor: 'move',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ flex: 1 }}>Prompt Card · 提示词卡片</span>
          <div
            className="nodrag"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 6,
              overflow: 'hidden',
              background: 'rgba(0,0,0,0.18)',
            }}
          >
            {(['simple', 'pro'] as const).map((mode) => {
              const active = promptMode === mode
              return (
                <button
                  key={mode}
                  type="button"
                  className="nodrag"
                  onClick={() => switchPromptMode(mode)}
                  title={mode === 'simple' ? '精简版' : '专业版'}
                  style={{
                    border: 'none',
                    background: active ? 'rgba(255,255,255,0.24)' : 'transparent',
                    color: '#fff',
                    padding: '3px 7px',
                    fontSize: 10,
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    lineHeight: 1.2,
                  }}
                >
                  {mode === 'simple' ? '精简' : '专业'}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            className="nodrag"
            onClick={handleDuplicate}
            onMouseDown={(e) => e.stopPropagation()}
            title="复制卡片"
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ⧉
          </button>
          <button
            type="button"
            className="nodrag"
            onClick={handleToggle}
            onMouseDown={(e) => e.stopPropagation()}
            title={collapsed ? '展开' : '最小化'}
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            {collapsed ? '+' : '−'}
          </button>
          <button
            type="button"
            className="nodrag"
            onClick={handleDelete}
            onMouseDown={(e) => e.stopPropagation()}
            title="删除卡片"
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              width: 22,
              height: 22,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>

        {!collapsed && (
          <>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {promptMode === 'simple' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ color: '#94a3b8', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                📜 完整提示词
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 10,
                    color: simpleTokens > TOKEN_RECOMMEND_MAX ? '#fbbf24' : '#64748b',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                  title={`推荐 ≤ ${TOKEN_RECOMMEND_MAX}，超出可能被截断`}
                >
                  ≈ {simpleTokens} tok
                </span>
              </span>
              <AutoGrowTextarea
                value={simplePrompt}
                placeholder="直接输入完整提示词…"
                onChange={(v) => updateData({ simplePrompt: v })}
                enableTabComplete
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: 10,
                  color: '#f1f5f9',
                  fontSize: 12,
                  outline: 'none',
                  fontFamily: 'JetBrains Mono, monospace',
                  lineHeight: 1.55,
                  minHeight: 104,
                }}
              />
              {simpleTokens > TOKEN_RECOMMEND_MAX ? (
                <span style={{ color: '#fbbf24', fontSize: 10 }}>
                  ⚠ 超过推荐 {TOKEN_RECOMMEND_MAX} tokens，模型可能截断尾部
                </span>
              ) : null}
            </label>
          ) : (
            <>
          {FIELD_GROUPS.map((group) => {
            const groupOpen = fieldGroupsOpen[group.key] ?? group.defaultOpen
            return (
              <section
                key={group.key}
                style={{
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: groupOpen ? 'rgba(15,23,42,0.55)' : 'rgba(2,6,23,0.35)',
                }}
              >
                <button
                  type="button"
                  className="nodrag"
                  onClick={() => toggleFieldGroup(group.key, !groupOpen)}
                  style={{
                    width: '100%',
                    border: 'none',
                    borderBottom: groupOpen ? '1px solid #1e293b' : 'none',
                    background: 'rgba(11,17,32,0.7)',
                    color: '#cbd5e1',
                    padding: '8px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ width: 16, color: groupOpen ? '#a78bfa' : '#64748b', fontSize: 13 }}>
                    {groupOpen ? '−' : '+'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 700 }}>{group.title}</span>
                    <span style={{ display: 'block', fontSize: 10, color: '#64748b', marginTop: 1 }}>
                      {group.description}
                    </span>
                  </span>
                  <span style={{ color: '#64748b', fontSize: 10 }}>{group.fields.length} fields</span>
                </button>

                {groupOpen ? (
                  <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {group.fields.map((fieldKey) => {
                      const f = FIELD_DEFS_BY_KEY.get(fieldKey)
                      if (!f) return null
                      const presetKey = (PROMPT_PRESETS[f.key as PresetKey] ? (f.key as PresetKey) : null)
                      const isOpen = presetKey !== null && openPresetKey === presetKey
                      return (
                        <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>
                            {f.icon} {f.label}
                          </span>
                          <AutoGrowTextarea
                            value={fields[f.key] ?? ''}
                            placeholder={f.placeholder}
                            onChange={(v) => updateField(f.key, v)}
                            enableTabComplete
                            onFocus={() => {
                              if (presetKey) setOpenPresetKey(presetKey)
                            }}
                            onBlur={() => {
                              if (presetKey && openPresetKey === presetKey) setOpenPresetKey(null)
                            }}
                            onUserKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setOpenPresetKey(null)
                                return
                              }
                              if (e.key === 'Tab' || e.key === 'Shift') return
                              if (presetKey && openPresetKey === presetKey) setOpenPresetKey(null)
                            }}
                            style={{
                              background: '#1e293b',
                              border: '1px solid #334155',
                              borderRadius: 6,
                              padding: '6px 8px',
                              color: '#f1f5f9',
                              fontSize: 12,
                              outline: 'none',
                              fontFamily: 'inherit',
                              lineHeight: 1.5,
                            }}
                          />
                          {isOpen && presetKey ? (
                            <PresetDropdown
                              items={PROMPT_PRESETS[presetKey]}
                              value={fields[f.key] ?? ''}
                              onSelect={(v) => {
                                updateField(f.key, v)
                                setOpenPresetKey(null)
                              }}
                            />
                          ) : null}
                        </label>
                      )
                    })}
                  </div>
                ) : null}
              </section>
            )
          })}

          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
              }}
            >
              <span style={{ color: '#94a3b8', fontSize: 11, flex: 1 }}>
                📜 字段拼装预览
                {useAiPrompt && aiPrompt ? (
                  <span style={{ color: '#64748b', marginLeft: 6 }}>（未启用）</span>
                ) : null}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: previewTokens > TOKEN_RECOMMEND_MAX ? '#fbbf24' : '#64748b',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
                title={`推荐 ≤ ${TOKEN_RECOMMEND_MAX}，超出可能被截断`}
              >
                ≈ {previewTokens} tok
              </span>
            </div>
            <div
              onClick={() => {
                if (aiPrompt && useAiPrompt) updateData({ useAiPrompt: false })
              }}
              style={{
                background: '#020617',
                border: useAiPrompt && aiPrompt ? '1px dashed #334155' : '1px solid #6366f1',
                borderRadius: 6,
                padding: 8,
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: useAiPrompt && aiPrompt ? '#64748b' : '#cbd5e1',
                minHeight: 48,
                maxHeight: 120,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                cursor: aiPrompt && useAiPrompt ? 'pointer' : 'default',
              }}
            >
              {preview || <span style={{ color: '#475569' }}>填写字段后这里会显示拼装结果…</span>}
            </div>
            {previewTokens > TOKEN_RECOMMEND_MAX ? (
              <div style={{ color: '#fbbf24', fontSize: 10, marginTop: 2 }}>
                ⚠ 超过推荐 {TOKEN_RECOMMEND_MAX} tokens，模型可能截断尾部
              </div>
            ) : null}
          </div>

          {aiPrompt ? (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 4,
                }}
              >
                <span style={{ color: '#a78bfa', fontSize: 11, flex: 1, fontWeight: 600 }}>
                  ✨ AI 优化版
                  {useAiPrompt ? (
                    <span style={{ color: '#22c55e', marginLeft: 6 }}>（已启用）</span>
                  ) : (
                    <span style={{ color: '#64748b', marginLeft: 6 }}>（未启用）</span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: aiTokens > TOKEN_RECOMMEND_MAX ? '#fbbf24' : '#64748b',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                >
                  ≈ {aiTokens} tok
                </span>
                <button
                  type="button"
                  className="nodrag"
                  onClick={() => updateData({ useAiPrompt: !useAiPrompt })}
                  style={{
                    background: useAiPrompt ? 'rgba(34,197,94,0.15)' : 'rgba(139,92,246,0.15)',
                    border: `1px solid ${useAiPrompt ? '#22c55e' : '#8b5cf6'}`,
                    color: useAiPrompt ? '#86efac' : '#c4b5fd',
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  {useAiPrompt ? '使用拼装版' : '改用此版本'}
                </button>
              </div>
              <div
                onClick={() => {
                  if (!useAiPrompt) updateData({ useAiPrompt: true })
                }}
                style={{
                  background: '#0b0820',
                  border: useAiPrompt ? '1px solid #8b5cf6' : '1px dashed #334155',
                  borderRadius: 6,
                  padding: 8,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                  color: useAiPrompt ? '#e9d5ff' : '#64748b',
                  minHeight: 48,
                  maxHeight: 140,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  cursor: !useAiPrompt ? 'pointer' : 'default',
                }}
              >
                {aiPrompt}
              </div>
            </div>
          ) : null}

          {optimizeError ? (
            <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>{optimizeError}</div>
          ) : null}
            </>
          )}

          {generationProgress ? (
            <div style={{ color: '#67e8f9', fontSize: 11, marginTop: 4 }}>{generationProgress}</div>
          ) : null}

          {lastError ? (
            <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>{lastError}</div>
          ) : null}
        </div>

        <div style={{ padding: 10, borderTop: '1px solid #1e293b', background: '#0b1120', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="nodrag" style={{ display: 'grid', gridTemplateColumns: '1fr 78px 92px', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>模型</span>
              <select
                value={model}
                onChange={(e) => updateData({ model: e.target.value })}
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              >
                {AVAILABLE_IMAGE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>数量</span>
              <select
                value={batchCount}
                onChange={(e) => updateData({ batchCount: Number(e.target.value) as 1 | 2 | 4 })}
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              >
                {BATCH_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count} 张</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 10 }}>Seed</span>
              <input
                value={seed}
                onChange={(e) => updateData({ seed: e.target.value.replace(/[^\d]/g, '') })}
                placeholder="随机"
                inputMode="numeric"
                style={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: '#cbd5e1',
                  padding: '6px 8px',
                  fontSize: 11,
                  outline: 'none',
                }}
              />
            </label>
          </div>
          {promptMode === 'pro' ? (
            <button
              type="button"
              className="nodrag"
              disabled={isOptimizing || !preview.trim()}
              onClick={handleOptimize}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #8b5cf6',
                borderRadius: 8,
                background: isOptimizing ? '#312e81' : 'rgba(139,92,246,0.12)',
                color: isOptimizing ? '#a5b4fc' : '#c4b5fd',
                fontWeight: 600,
                fontSize: 12,
                cursor: isOptimizing || !preview.trim() ? 'not-allowed' : 'pointer',
                letterSpacing: 0.3,
              }}
            >
              {isOptimizing ? '✨ 正在优化…' : aiPrompt ? '✨ 重新 AI 优化' : '✨ AI 优化提示词'}
            </button>
          ) : null}
          <button
            type="button"
            disabled={isGenerating}
            onClick={handleGenerate}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: 'none',
              borderRadius: 8,
              background: isGenerating
                ? '#475569'
                : 'linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              letterSpacing: 0.5,
            }}
          >
            {isGenerating
              ? batchCount > 1 ? '⏳ 批量生成中…' : '⏳ 生成中…'
              : promptMode === 'simple'
                ? `⚡ 生成 ${batchCount} 张`
                : useAiPrompt && aiPrompt
                ? `⚡ 用 AI 优化版生成 ${batchCount} 张`
                : `⚡ 用拼装版生成 ${batchCount} 张`}
          </button>
        </div>
          </>
        )}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: '#8b5cf6', width: 10, height: 10 }} />
    </>
  )
}
